import docsManifest from '../../documentation/docs-manifest.json';

const markdownContext = require.context('../../documentation', true, /\.md$/);

const SKIP_MD_FILES = ['_TEMPLATE.md', 'README.md'];

export const getDocsManifest = () => docsManifest;

export const getManifestSettings = () => docsManifest.settings || {};

export const getDocumentById = (docId) =>
  (docsManifest.documents || []).find((doc) => doc.id === docId) || null;

export const loadMarkdownContent = (relativePath) => {
  if (!relativePath) return null;
  try {
    const normalized = relativePath.replace(/^\.\//, '');
    const key = `./${normalized}`;
    const raw = markdownContext(key);

    if (typeof raw !== 'string' || raw.length === 0) {
      return null;
    }

    // Si webpack devolvió URL de asset en lugar del texto, el contenido no es usable.
    if (
      raw.startsWith('/static/')
      || raw.startsWith('static/')
      || /^https?:\/\/.+\.md(\?|$)/i.test(raw)
    ) {
      console.warn('[documentation] Markdown import returned asset URL, expected raw source:', raw);
      return null;
    }

    return raw;
  } catch (error) {
    return null;
  }
};

export const parseFrontmatter = (rawContent) => {
  if (!rawContent) {
    return { frontmatter: {}, body: '' };
  }

  const match = rawContent.match(/^---\r?\n([\s\S]*?)\r?\n---\r?\n([\s\S]*)$/);
  if (!match) {
    return { frontmatter: {}, body: rawContent };
  }

  const fmText = match[1];
  const body = match[2];
  const frontmatter = {};

  const scalarFields = [
    'docId', 'title', 'audience', 'version', 'createdAt', 'updatedAt',
    'author', 'status', 'supersededBy',
  ];

  scalarFields.forEach((field) => {
    const scalarMatch = fmText.match(new RegExp(`^${field}:\\s*"?([^"\\n]+)"?`, 'm'));
    if (scalarMatch) {
      frontmatter[field] = scalarMatch[1].trim();
    }
  });

  const rolesMatch = fmText.match(/roles:\s*\n((?:\s+-\s+.+\n?)+)/);
  if (rolesMatch) {
    frontmatter.roles = rolesMatch[1]
      .split('\n')
      .map((line) => line.replace(/^\s*-\s*/, '').trim())
      .filter(Boolean);
  }

  const tagsMatch = fmText.match(/tags:\s*\n((?:\s+-\s+.+\n?)+)/);
  if (tagsMatch) {
    frontmatter.tags = tagsMatch[1]
      .split('\n')
      .map((line) => line.replace(/^\s*-\s*/, '').trim())
      .filter(Boolean);
  }

  const relatedMatch = fmText.match(/relatedDocs:\s*\n((?:\s+-\s+.+\n?)+)/);
  if (relatedMatch) {
    frontmatter.relatedDocs = relatedMatch[1]
      .split('\n')
      .map((line) => line.replace(/^\s*-\s*/, '').trim())
      .filter(Boolean);
  }

  const legacyMatch = fmText.match(/sourceLegacy:\s*\n((?:\s+-\s+.+\n?)+)/);
  if (legacyMatch) {
    frontmatter.sourceLegacy = legacyMatch[1]
      .split('\n')
      .map((line) => line.replace(/^\s*-\s*/, '').trim())
      .filter(Boolean);
  }

  return { frontmatter, body };
};

const isAssignEndUserDocsToAdminEnabled = (configurationState = {}) => {
  const raw = configurationState?.assignEndUserDocumentationToAdministratorEnabled;
  if (raw === undefined || raw === null || raw === '') {
    return true;
  }
  return String(raw).toLowerCase() === 'true';
};

const isDocumentationWikiEndUserEnabled = (configurationState = {}) => {
  const raw = configurationState?.documentationWikiEndUserEnabled;
  if (raw === undefined || raw === null || raw === '') {
    return false;
  }
  return String(raw).toLowerCase() === 'true';
};

export const resolveUserDocRoles = (roleLogin = {}, configurationState = {}) => {
  const matched = [];
  const roleMap = docsManifest.roles || {};

  Object.entries(roleMap).forEach(([roleKey, roleDef]) => {
    const flags = roleDef.flags || [];
    const hasFlag = flags.some((flag) => roleLogin[flag] === true);
    if (hasFlag) {
      matched.push(roleKey);
    }
  });

  if (!isDocumentationWikiEndUserEnabled(configurationState)) {
    const endUserIndex = matched.indexOf('end_user');
    if (endUserIndex >= 0) {
      matched.splice(endUserIndex, 1);
    }
  }

  if (
    roleLogin.isAdministrator
    && isAssignEndUserDocsToAdminEnabled(configurationState)
    && !matched.includes('end_user')
  ) {
    matched.push('end_user');
  }

  return matched;
};

export const userCanAccessDocument = (doc, roleLogin = {}, configurationState = {}) => {
  if (!doc) return false;
  if (!doc.enabled) return false;

  const userRoles = resolveUserDocRoles(roleLogin, configurationState);
  if (!doc.roles || doc.roles.length === 0) return true;

  return doc.roles.some((roleKey) => userRoles.includes(roleKey));
};

export const isTransversalNavVisible = (roleLogin = {}, configurationState = {}) =>
  resolveUserDocRoles(roleLogin, configurationState).length > 0;

const filterNavNode = (node, roleLogin, documentsMap, configurationState) => {
  if (node.docId) {
    const doc = documentsMap[node.docId];
    if (!doc || !doc.enabled) return null;
    if (!userCanAccessDocument(doc, roleLogin, configurationState)) return null;
    return { ...node };
  }

  if (!node.children || node.children.length === 0) return null;

  const children = node.children
    .map((child) => filterNavNode(child, roleLogin, documentsMap, configurationState))
    .filter(Boolean);

  if (children.length === 0) return null;
  return { ...node, children };
};

export const buildNavigationForUser = (roleLogin = {}, configurationState = {}) => {
  const documentsMap = {};
  (docsManifest.documents || []).forEach((doc) => {
    documentsMap[doc.id] = doc;
  });
  const userRoles = resolveUserDocRoles(roleLogin, configurationState);

  return (docsManifest.navigation || [])
    .map((navRoot) => {
      if (navRoot.roleKey === '_transversal') {
        if (!isTransversalNavVisible(roleLogin, configurationState)) return null;
      } else if (!userRoles.includes(navRoot.roleKey)) {
        return null;
      }

      const children = (navRoot.children || [])
        .map((child) => filterNavNode(child, roleLogin, documentsMap, configurationState))
        .filter(Boolean);

      if (children.length === 0) return null;
      return { ...navRoot, children };
    })
    .filter(Boolean)
    .sort((a, b) => {
      if (a.roleKey === '_transversal') return -1;
      if (b.roleKey === '_transversal') return 1;
      return 0;
    });
};

export const LANDING_DOC_ID = 'documentacion-inicio';

export const findFirstAccessibleDocId = (roleLogin = {}, configurationState = {}) => {
  const landingDoc = getDocumentById(LANDING_DOC_ID);
  if (landingDoc && userCanAccessDocument(landingDoc, roleLogin, configurationState)) {
    return LANDING_DOC_ID;
  }

  const nav = buildNavigationForUser(roleLogin, configurationState);

  const walk = (nodes) => {
    for (const node of nodes) {
      if (node.docId) return node.docId;
      if (node.children) {
        const found = walk(node.children);
        if (found) return found;
      }
    }
    return null;
  };

  for (const root of nav) {
    const found = walk(root.children || []);
    if (found) return found;
  }

  return null;
};

export const getStatusLabelKey = (status) => {
  switch (status) {
    case 'deprecated':
      return 'docs.status.deprecated';
    case 'historic':
      return 'docs.status.historic';
    case 'draft':
      return 'docs.status.draft';
    default:
      return 'docs.status.published';
  }
};

export const listMarkdownKeys = () =>
  markdownContext.keys().filter((key) => {
    const fileName = key.split('/').pop();
    return !SKIP_MD_FILES.includes(fileName);
  });

const normalizeHeadingText = (value = '') =>
  value
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ');

export const stripDuplicateDocumentTitle = (body, title) => {
  if (!body || !title) return body || '';

  const targetTitle = normalizeHeadingText(title);
  const lines = body.split('\n');
  let index = 0;

  while (index < lines.length && lines[index].trim() === '') {
    index += 1;
  }

  const headingMatch = lines[index]?.match(/^#\s+(.+)$/);
  if (headingMatch && normalizeHeadingText(headingMatch[1]) === targetTitle) {
    index += 1;
    while (index < lines.length && lines[index].trim() === '') {
      index += 1;
    }
    return lines.slice(index).join('\n');
  }

  return body;
};

export const splitDocumentationSections = (body) => {
  if (!body) return [''];

  return body
    .split(/\n\s*<!--\s*docs-page-break\s*-->\s*\n/g)
    .map((section) => section.trim())
    .filter((section, index, array) => section.length > 0 || array.length === 1);
};

export const prepareDocumentationMarkdown = (body, title) => {
  const withoutDuplicateTitle = stripDuplicateDocumentTitle(body, title);
  return splitDocumentationSections(withoutDuplicateTitle);
};

/** Cuenta hojas con docId en un subárbol de navegación. */
export const countNavigationDocLeaves = (nodes = []) => {
  let count = 0;
  nodes.forEach((node) => {
    if (node.docId) {
      count += 1;
    } else if (node.children?.length) {
      count += countNavigationDocLeaves(node.children);
    }
  });
  return count;
};

/** IDs de nodos agrupadores (expandibles) en un subárbol. */
export const collectExpandableNodeIds = (nodes = [], ids = []) => {
  nodes.forEach((node) => {
    if (!node.docId && node.children?.length) {
      ids.push(node.id);
      collectExpandableNodeIds(node.children, ids);
    }
  });
  return ids;
};

/** Filtra árbol de nav dejando solo ramas que contienen docIds coincidentes. */
export const filterNavigationByDocIds = (nodes = [], matchingDocIds) => {
  if (!matchingDocIds || matchingDocIds.size === 0) return [];

  return nodes
    .map((node) => {
      if (node.docId) {
        return matchingDocIds.has(node.docId) ? { ...node } : null;
      }

      const children = filterNavigationByDocIds(node.children || [], matchingDocIds);
      if (children.length === 0) return null;
      return { ...node, children };
    })
    .filter(Boolean);
};

/** Índice de búsqueda: documentos markdown accesibles para el usuario. */
export const buildSearchIndexForUser = (roleLogin = {}, configurationState = {}) =>
  (docsManifest.documents || [])
    .filter((doc) => doc.enabled && doc.type !== 'externalLink' && userCanAccessDocument(doc, roleLogin, configurationState))
    .map((doc) => {
      const raw = loadMarkdownContent(doc.file);
      if (!raw) return null;

      const { frontmatter, body } = parseFrontmatter(raw);
      return {
        docId: doc.id,
        title: frontmatter.title || doc.id,
        body: body || '',
        tags: frontmatter.tags || [],
      };
    })
    .filter(Boolean);

/**
 * Busca texto en título, cuerpo, tags y docId de la wiki accesible.
 * @returns {{ count: number, matchingDocIds: Set<string> }}
 */
export const searchWikiDocuments = (roleLogin = {}, query = '', configurationState = {}) => {
  const normalized = (query || '').trim().toLowerCase();
  if (!normalized) {
    return { count: 0, matchingDocIds: new Set() };
  }

  const index = buildSearchIndexForUser(roleLogin, configurationState);
  const matchingDocIds = new Set();

  index.forEach((item) => {
    const haystack = [
      item.docId,
      item.title,
      item.body,
      ...(item.tags || []),
    ]
      .join('\n')
      .toLowerCase();

    if (haystack.includes(normalized)) {
      matchingDocIds.add(item.docId);
    }
  });

  return { count: matchingDocIds.size, matchingDocIds };
};
